/*
  The following utility functions are used in our results pages and reducer
*/

/**
 * Calculate an individual's odds ratio from:
 *   - sd: standard deviations from the mean
 *   - orPerSD: published odds ratio per 1 SD
 *
 * Example:
 *   sd = 1.75
 *   orPerSD = 1.26
 *   returns 1.50
 */
export const sdToOR = (sd, orPerSD, higherIsRisk = true) => {
  const z = Number(sd);
  const or = Number(orPerSD);

  if (!Number.isFinite(z) || !Number.isFinite(or) || or <= 0) {
    return null;
  }

  return Math.pow(or, higherIsRisk ? z : -z);
};

/**
 * Convert a percentile to a z-score (standard deviations from the mean).
 *
 * Accepts either:
 *   96   = 96th percentile
 *   0.96 = 96th percentile
 *
 * Uses Peter John Acklam's inverse-normal approximation.
 */
export const percentileToSD = (percentile) => {
  let probability = Number(percentile);

  if (!Number.isFinite(probability)) {
    return null;
  }

  // Support both 96 and 0.96.
  if (probability > 1) {
    probability /= 100;
  }

  // The inverse normal is undefined at exactly 0 and 1.
  const epsilon = 1e-10;
  probability = Math.min(Math.max(probability, epsilon), 1 - epsilon);

  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2,
    1.38357751867269e2, -3.066479806614716e1, 2.506628277459239,
  ];

  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2,
    6.680131188771972e1, -1.328068155288572e1,
  ];

  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838,
    -2.549732539343734, 4.374664141464968, 2.938163982698783,
  ];

  const d = [
    7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996,
    3.754408661907416,
  ];

  const lowerLimit = 0.02425;
  const upperLimit = 1 - lowerLimit;

  if (probability < lowerLimit) {
    const q = Math.sqrt(-2 * Math.log(probability));

    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }

  if (probability <= upperLimit) {
    const q = probability - 0.5;
    const r = q * q;

    return (
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) *
        q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
    );
  }

  const q = Math.sqrt(-2 * Math.log(1 - probability));

  return -(
    (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
    ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
  );
};

/**
 * Format the percentile and corresponding standard-deviation text.
 */
export const getPercentileSDText = (percentile, decimals = 2) => {
  const numericPercentile = Number(percentile);

  if (!Number.isFinite(numericPercentile)) {
    return "";
  }

  const displayedPercentile =
    numericPercentile <= 1
      ? Math.round(numericPercentile * 100)
      : Math.round(numericPercentile);

  const sd = percentileToSD(numericPercentile);

  if (sd === null) {
    return `You are in the ${displayedPercentile}th percentile.`;
  }

  return `You are in the ${displayedPercentile}th percentile, which is ${sd.toFixed(
    decimals,
  )} standard deviations from the mean.`;
};

// finds bucket for use in the polygenic histograms
export const findBucket = (score, series) => {
  return series.reduce((agg, cur, i) => {
    if (i === series.length - 1) {
      // For last item, score only needs to be greater
      if (score > parseFloat(cur)) return i;
    } else {
      if (score > parseFloat(cur)) return i;
    }
    return agg;
  }, 0);
};

// returns text showing a positive number and associated Increased/Decreased tag for readability in text
//getRiskText is for fracture risk and getOsteoRiskText is for osteoporosis risk
export const getRiskText = (deltaRisk) => {
  if (deltaRisk < 0) {
    return deltaRisk * -1 + "% decreased";
  }
  return deltaRisk + "% increased";
};
export const getOsteoRiskText = (deltaRisk) => {
  if (deltaRisk < 0) {
    return deltaRisk * -1 + " fold decreased";
  }
  return deltaRisk + " fold increased";
};

// validates demogs
export const validateDemographics = (dg) => {
  if (dg && Object.keys(dg).length > 0) {
    try {
      if (
        dg.height > 0 &&
        dg.weight > 0 &&
        dg.gender.length > 0 &&
        dg.birthYear > 0 &&
        ["male", "female"].indexOf(dg.gender) > -1
      ) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }
  return false;
};

// validateDemogs not used because sometimes only gender is specified.
export const validateGender = (gen) => {
  if (gen && Object.keys(gen).length > 0) {
    try {
      if (["male", "female"].indexOf(gen.gender) > -1) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }
  return false;
};

// gets suffix for a given percentile
export const getPercentileSuffix = (percentile) => {
  if (percentile === undefined || percentile === null) {
    return "";
  }

  const percentileString = percentile.toString();

  if (
    percentileString.endsWith("11") ||
    percentileString.endsWith("12") ||
    percentileString.endsWith("13")
  ) {
    return "th";
  }

  if (percentileString.endsWith("1")) return "st";
  if (percentileString.endsWith("2")) return "nd";
  if (percentileString.endsWith("3")) return "rd";

  return "th";
};

// finds the unformatted percentile
const percentileConfig = require("../../json/percentile-config.json");
export const getPercentile = (score, configKey) => {
  const { binScore, binSize } = percentileConfig[configKey];
  let scoreIdx = findBucket(score, binScore);
  let totalSum = binSize.reduce((acc, el) => acc + el);
  let bucketSum = binSize.reduce((total, curEl, index) => {
    if (index > scoreIdx) {
      return total;
    }
    return total + curEl;
  });
  return ((bucketSum / totalSum) * 100).toFixed(0);
};

// finds the percentile for tags that have a percentile value in upload_entry_results
export const getPercentileText2 = (percentile) => {
  if (percentile === undefined || percentile === null) {
    console.warn(
      "getPercentileText2: percentile is undefined or null",
      "percentile =", percentile
    );
    return null;
  }

  const suffix = getPercentileSuffix(percentile);

  return `${percentile}${suffix} percentile`;
};

// finds the percentile and formats it for presentational purposes
export const getPercentileText = (tag, score, gender = null) => {
  let configKey;

  if (tag === "testosterone" && gender !== null) {
    configKey = `${gender}-testosterone`;
  } else {
    configKey = tag;
  }

  const percentile = getPercentile(score, configKey);

  if (percentile === undefined || percentile === null) {
    console.warn(
      "getPercentileText: percentile is undefined",
      "tag =",
      tag,
      "score =",
      score,
      "configKey =",
      configKey,
    );
    return null;
  }

  const suffix = getPercentileSuffix(percentile);

  return `${percentile}${suffix} percentile`;
};

// finds relative risk based on a genetic score for an injury
export const getRelativeRisk = (riskVal) => {
  let relRisk;
  if (riskVal > 0) {
    relRisk = `${(Math.abs(riskVal) * 100).toFixed(0)}% increased`;
  } else if (riskVal < 0) {
    relRisk = `${(Math.abs(riskVal) * 100).toFixed(0)}% decreased`;
  } else {
    relRisk = "normal";
  }
  return relRisk;
};

// finds incidence based on a genetic score for an injury
export const getIncidence = (riskVal) => {
  return (riskVal + 1).toFixed(2) + "x";
};

export function prob_dens_function(mean, sd, percentile) {
  // Constants
  const pi = Math.PI;
  const e = Math.E;

  // Calculate the standard score (z-score) corresponding to the percentile
  const zScore = Math.sqrt(2) * inverseErrorFunction(2 * percentile - 1);
  console.log(`The zscore for ${percentile} percentile is: ${zScore}`);
  // Calculate the y value using the PDF formula
  const yValue =
    (1 / (sd * Math.sqrt(2 * pi))) *
    Math.pow(e, -0.5 * Math.pow((zScore - mean) / sd, 2));

  return yValue;
}

// Inverse error function for calculating z-score
// This function is used internally by the prob_dens_function
function inverseErrorFunction(x) {
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);

  const t = 1.0 / (1.0 + p * x);
  const y = (((a5 * t + a4) * t + a3) * t + a2) * t + a1;

  return sign * (1.0 - y * Math.pow(Math.E, -x * x));
}
